const LOCK_PREFIX = 'niwariPopupLock:'
const CHANNEL_NAME = 'niwari_popup_coordination'
const LOCK_TTL_MS = 30 * 1000
const HEARTBEAT_MS = 10 * 1000

const makeInstanceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const lockKey = (draftId) => `${LOCK_PREFIX}${String(draftId)}`

const readLock = (draftId) => {
  try {
    const raw = localStorage.getItem(lockKey(draftId))
    if (!raw) return null
    const value = JSON.parse(raw)
    if (!value?.ownerId || Number(value.expiresAt) <= Date.now()) {
      localStorage.removeItem(lockKey(draftId))
      return null
    }
    return value
  } catch (_) {
    return null
  }
}

const writeLock = (draftId, ownerId) => {
  const value = {
    draftId: String(draftId),
    ownerId,
    expiresAt: Date.now() + LOCK_TTL_MS
  }
  try {
    localStorage.setItem(lockKey(draftId), JSON.stringify(value))
    return value
  } catch (_) {
    return null
  }
}

const postMessage = (message) => {
  if (typeof BroadcastChannel === 'undefined') return
  const channel = new BroadcastChannel(CHANNEL_NAME)
  channel.postMessage(message)
  channel.close()
}

/**
 * Reserve a draft before opening its popup. The popup takes over the same
 * lease immediately after it mounts.
 */
export const acquirePopupLease = (draftId) => {
  const existing = readLock(draftId)
  if (existing) return { acquired: false, lock: existing }

  const ownerId = makeInstanceId()
  const lock = writeLock(draftId, ownerId)
  const verified = readLock(draftId)
  if (!lock || !verified || verified.ownerId !== ownerId) {
    return { acquired: false, lock: verified }
  }

  postMessage({ type: 'claim', draftId: String(draftId), ownerId })
  return { acquired: true, ownerId }
}

export const releasePopupLease = (draftId, ownerId) => {
  const existing = readLock(draftId)
  if (existing?.ownerId !== ownerId) return
  try {
    localStorage.removeItem(lockKey(draftId))
  } catch (_) {}
  postMessage({ type: 'release', draftId: String(draftId), ownerId })
}

export const requestPopupFocus = (draftId) => {
  postMessage({ type: 'focus-request', draftId: String(draftId) })
}

/**
 * Start ownership from the popup itself. Returns a cleanup function when the
 * popup owns the lease; otherwise the caller must close without loading data.
 */
export const activatePopupLease = ({
  draftId,
  ownerId,
  onFocusRequest,
  onLeaseLost
}) => {
  if (!draftId || !ownerId) return { active: false, release: () => {} }

  const existing = readLock(draftId)
  if (existing && existing.ownerId !== ownerId) {
    requestPopupFocus(draftId)
    return { active: false, release: () => {} }
  }
  if (!writeLock(draftId, ownerId)) {
    return { active: false, release: () => {} }
  }

  let released = false
  const loseLease = () => {
    if (released) return
    released = true
    window.clearInterval(heartbeat)
    channel?.removeEventListener('message', handleMessage)
    channel?.close()
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener('visibilitychange', renewOnForeground)
    window.removeEventListener('focus', renewOnForeground)
    window.removeEventListener('pagehide', release)
    onLeaseLost?.()
  }
  const renew = () => {
    const current = readLock(draftId)
    // A delayed timer can let our lock expire while this popup is busy
    // rendering a large draft. An absent/expired lock is not evidence that a
    // different popup owns the draft, so reclaim it before deciding to close.
    if (current && current.ownerId !== ownerId) {
      loseLease()
      return
    }

    if (!writeLock(draftId, ownerId)) {
      loseLease()
      return
    }

    // localStorage has no atomic compare-and-set. Verify ownership after
    // writing so a genuine concurrent claimant still wins deterministically.
    const verified = readLock(draftId)
    if (!verified || verified.ownerId !== ownerId) {
      loseLease()
    }
  }
  const renewOnForeground = () => {
    if (document.visibilityState === 'visible') renew()
  }
  const heartbeat = window.setInterval(renew, HEARTBEAT_MS)
  const channel =
    typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel(CHANNEL_NAME)
  const handleMessage = (event) => {
    const message = event?.data
    if (
      message?.type === 'focus-request' &&
      String(message.draftId) === String(draftId)
    ) {
      onFocusRequest?.()
      channel?.postMessage({
        type: 'focus-ack',
        draftId: String(draftId),
        ownerId
      })
    }
  }
  channel?.addEventListener('message', handleMessage)

  const handleStorage = (event) => {
    if (event.key !== lockKey(draftId)) return
    const current = readLock(draftId)
    if (current && current.ownerId !== ownerId) loseLease()
  }
  window.addEventListener('storage', handleStorage)
  window.addEventListener('visibilitychange', renewOnForeground)
  window.addEventListener('focus', renewOnForeground)

  const release = () => {
    if (released) return
    released = true
    window.clearInterval(heartbeat)
    channel?.removeEventListener('message', handleMessage)
    channel?.close()
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener('visibilitychange', renewOnForeground)
    window.removeEventListener('focus', renewOnForeground)
    releasePopupLease(draftId, ownerId)
  }

  window.addEventListener('pagehide', release, { once: true })
  return {
    active: true,
    release: () => {
      window.removeEventListener('pagehide', release)
      release()
    }
  }
}
